import React, { useRef, useState, useEffect, useImperativeHandle } from 'react'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { ActionMenuItem } from '@/shared/components/molecules/ActionDropdownMenu'
import { cn } from '@/shared/utils/helpers'

import PlaylistCard from './PlaylistCard'
import PlaylistSkeleton from './PlaylistSkeleton'

const DESKTOP_BREAKPOINT_PX = 768

export interface PlaylistCarouselProps {
	playlists: Playlist[]
	dropdownOptions?: ActionMenuItem[]
	getDropdownOptions?: (playlist: Playlist) => ActionMenuItem[]
	onFavoriteClick?: (playlist: Playlist, isFavoritesCarousel: boolean) => void
	showLocationIcon?: boolean
	showFavoriteIcon?: boolean
	isLoading?: boolean
	itemsPerPage?: number
	className?: string
	onScrollStateChange?: (
		canScrollLeft: boolean,
		canScrollRight: boolean
	) => void
	loadingPlaylistId?: number | null
	isFavoritesCarousel?: boolean
	favoritePlaylistIds?: Set<number>
	isCustomPlaylist?: boolean
}

export interface PlaylistCarouselRef {
	scrollLeft: () => void
	scrollRight: () => void
	canScrollLeft: boolean
	canScrollRight: boolean
}

const PlaylistCarousel = React.forwardRef<
	PlaylistCarouselRef,
	PlaylistCarouselProps
>(
	(
		{
			playlists,
			dropdownOptions = [],
			getDropdownOptions,
			onFavoriteClick,
			showLocationIcon = false,
			showFavoriteIcon = true,
			isLoading = false,
			itemsPerPage = 5,
			className = '',
			onScrollStateChange,
			loadingPlaylistId,
			isFavoritesCarousel = false,
			favoritePlaylistIds,
			isCustomPlaylist = false,
		},
		ref
	) => {
		const scrollContainerRef = useRef<HTMLDivElement>(null)
		const itemsRef = useRef<(HTMLDivElement | null)[]>([])
		const [canScrollLeft, setCanScrollLeft] = useState(false)
		const [canScrollRight, setCanScrollRight] = useState(false)
		const [isDesktopViewport, setIsDesktopViewport] = useState(
			typeof window !== 'undefined'
				? window.innerWidth >= DESKTOP_BREAKPOINT_PX
				: false
		)
		// Extra padding at the end of the row so that, at the far right,
		// we can scroll enough to fully hide the "extra" card on the left.
		// Without this, the browser clamps scrollLeft before the last
		// full set of cards can align, and a 3rd-from-last card peeks in.
		const [tailPadding, setTailPadding] = useState(0)
		const [visibleOverflow, setVisibleOverflow] = useState(0)

		// Track the index of the first "page" item we scroll to.
		// Using a ref avoids re-renders while keeping navigation deterministic.
		const currentStartIndexRef = useRef(0)

		// Rough estimate of how many cards fit in the viewport at once.
		// We measure it from DOM once playlists are rendered so that
		// the last "page" can be clamped and off-screen cards fully hide.
		const itemsPerViewRef = useRef(1)

		itemsRef.current = []

		useEffect(() => {
			if (typeof window === 'undefined') return

			const updateViewport = () => {
				setIsDesktopViewport(window.innerWidth >= DESKTOP_BREAKPOINT_PX)
			}

			updateViewport()
			window.addEventListener('resize', updateViewport)
			window.addEventListener('orientationchange', updateViewport)

			return () => {
				window.removeEventListener('resize', updateViewport)
				window.removeEventListener('orientationchange', updateViewport)
			}
		}, [])

		useEffect(() => {
			if (!scrollContainerRef.current || itemsRef.current.length === 0) {
				setTailPadding(0)
				setVisibleOverflow(0)
				return
			}

			const firstItem = itemsRef.current[0]
			if (!firstItem) return

			const measureLayout = () => {
				const container = scrollContainerRef.current
				if (!container) return

				const containerWidth = container.clientWidth
				const itemWidth = firstItem.offsetWidth || 1
				const secondItem = itemsRef.current[1]
				const gap =
					secondItem && secondItem.offsetLeft > firstItem.offsetLeft
						? secondItem.offsetLeft - firstItem.offsetLeft - itemWidth
						: 0
				const itemStride = itemWidth + gap

				// Include gap in the layout math so the last card can be fully
				// visible without clipping.
				const estimatedPerView = Math.max(
					1,
					Math.floor((containerWidth + gap) / itemStride)
				)

				itemsPerViewRef.current = estimatedPerView

				// Keep desktop end-alignment behavior for button-driven paging only.
				// Mobile uses native snap without artificial width/padding tweaks.
				if (!isDesktopViewport) {
					setTailPadding(0)
					setVisibleOverflow(0)
					return
				}

				// Compute how much "fake" space we need to add at the end so that
				// the last full set of cards can align perfectly in the viewport.
				const neededPadding = Math.max(
					0,
					containerWidth - estimatedPerView * itemStride + gap + 1 // +1 as small safety buffer
				)
				// Ensure the last card can be fully visible at the far right.
				const minEndPadding = itemStride
				const finalPadding = Math.max(neededPadding, minEndPadding)
				setTailPadding(finalPadding)
				// Let the scroll container extend so the last card can be fully visible
				// even when it would otherwise overflow the viewport.
				setVisibleOverflow(itemStride)
			}

			measureLayout()

			let resizeObserver: ResizeObserver | null = null
			if (typeof ResizeObserver !== 'undefined') {
				resizeObserver = new ResizeObserver(() => measureLayout())
				resizeObserver.observe(scrollContainerRef.current)
			}

			window.addEventListener('resize', measureLayout)
			window.addEventListener('orientationchange', measureLayout)

			return () => {
				resizeObserver?.disconnect()
				window.removeEventListener('resize', measureLayout)
				window.removeEventListener('orientationchange', measureLayout)
			}
		}, [playlists.length, isDesktopViewport])

		// Helper to clamp the start index so that:
		// - it never goes below 0
		// - at the far right, we never start so early that an extra
		//   "third from the end" card is still peeking in on the left.
		const getMaxStartIndex = () =>
			Math.max(0, playlists.length - itemsPerViewRef.current)

		const clampStartIndex = (index: number) => {
			const maxStart = getMaxStartIndex()
			if (index < 0) return 0
			if (index > maxStart) return maxStart
			return index
		}

		// Scroll so that the card at `startIndex` is aligned as the first visible card.
		const scrollToStartIndex = (startIndex: number) => {
			const container = scrollContainerRef.current
			if (!container) return

			const maxStart = getMaxStartIndex()
			if (startIndex >= maxStart) {
				const endIndex = Math.min(
					playlists.length - 1,
					startIndex + itemsPerViewRef.current - 1
				)
				const endItem = itemsRef.current[endIndex]
				if (!endItem) return
				const rightAlignedLeft =
					endItem.offsetLeft + endItem.offsetWidth - container.clientWidth

				container.scrollTo({
					left: Math.max(0, rightAlignedLeft),
					behavior: 'smooth',
				})
				return
			}

			const item = itemsRef.current[startIndex]
			if (!item) return

			container.scrollTo({
				left: item.offsetLeft,
				behavior: 'smooth',
			})
		}

		// Expose navigation methods via ref (for client-side scrolling)
		useImperativeHandle(
			ref,
			() => ({
				scrollLeft: () => {
					// Move one "page" to the left, clamped so we never underflow.
					currentStartIndexRef.current = clampStartIndex(
						currentStartIndexRef.current - itemsPerViewRef.current
					)
					scrollToStartIndex(currentStartIndexRef.current)
				},
				scrollRight: () => {
					// Move one "page" to the right, clamped so that at the
					// edge of the array we land on a position where any
					// extra card to the left is fully off-screen.
					currentStartIndexRef.current = clampStartIndex(
						currentStartIndexRef.current + itemsPerViewRef.current
					)
					scrollToStartIndex(currentStartIndexRef.current)
				},
				canScrollLeft,
				canScrollRight,
			}),
			[canScrollLeft, canScrollRight]
		)

		useEffect(() => {
			const observer = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							entry.target.classList.add('opacity-100')
							entry.target.classList.remove('opacity-40')
						} else {
							entry.target.classList.remove('opacity-100')
							entry.target.classList.add('opacity-40')
						}
					})
				},
				{
					root: scrollContainerRef.current,
					threshold: 0.4,
					rootMargin: '0px -10px 0px -10px',
				}
			)

			itemsRef.current.forEach((item) => {
				if (item) observer.observe(item)
			})

			return () => observer.disconnect()
		}, [playlists])

		// Check scroll position for client-side scrolling fallback
		useEffect(() => {
			const checkScrollPosition = () => {
				if (!scrollContainerRef.current) return

				const { scrollLeft, scrollWidth, clientWidth } =
					scrollContainerRef.current

				const newCanScrollLeft = scrollLeft > 0
				const newCanScrollRight = scrollLeft < scrollWidth - clientWidth - 10

				setCanScrollLeft(newCanScrollLeft)
				setCanScrollRight(newCanScrollRight)

				if (onScrollStateChange) {
					onScrollStateChange(newCanScrollLeft, newCanScrollRight)
				}
			}

			const container = scrollContainerRef.current
			if (container) {
				container.addEventListener('scroll', checkScrollPosition)
				checkScrollPosition()
			}

			return () => {
				if (container) {
					container.removeEventListener('scroll', checkScrollPosition)
				}
			}
		}, [playlists, onScrollStateChange])

		if (isLoading) {
			return <PlaylistSkeleton count={itemsPerPage} className={className} />
		}

		if (!playlists || playlists.length === 0) {
			return null
		}

		return (
			<div className={cn('relative w-full', className)}>
				<div
					ref={scrollContainerRef}
					className={cn(
						'flex-1 overflow-x-auto overflow-y-hidden py-2 m-0',
						'[&::-webkit-scrollbar]:hidden [scrollbar-width:none] [-ms-overflow-style:none]',
						'snap-x snap-mandatory scroll-smooth',
						'md:m-0' // Using md breakpoint for tablet fallback from styled-components theme
					)}
					style={
						visibleOverflow > 0
							? {
									width: `calc(100% + ${visibleOverflow}px)`,
								}
							: undefined
					}
				>
					<div
						className="flex gap-4 min-w-min"
						style={{
							// This extra padding only affects the far-right edge:
							// it doesn't change card layout, but it extends the
							// scrollable area so the last page can be reached.
							paddingRight: tailPadding,
						}}
					>
						{playlists.map((playlist, index) => {
							const isFavorite = favoritePlaylistIds
								? favoritePlaylistIds.has(playlist.id)
								: isFavoritesCarousel
							return (
								<div
									key={playlist.id}
									ref={(el) => {
										if (el) itemsRef.current[index] = el
									}}
									className={cn(
										'shrink-0 flex snap-start w-[160px] lg:w-[200px]',
										'transition-opacity duration-500 ease-[cubic-bezier(0.2,0.8,0.2,1)]',
										'opacity-100'
									)}
								>
									<PlaylistCard
										playlist={playlist}
										dropdownOptions={
											getDropdownOptions
												? getDropdownOptions(playlist)
												: dropdownOptions
										}
										onFavoriteClick={onFavoriteClick}
										showLocationIcon={showLocationIcon}
										showFavoriteIcon={showFavoriteIcon}
										isLoadingFavorite={loadingPlaylistId === playlist.id}
										isFavoritesCarousel={isFavoritesCarousel}
										isFavorite={isFavorite}
										isCustomPlaylist={isCustomPlaylist}
									/>
								</div>
							)
						})}
					</div>
				</div>
			</div>
		)
	}
)

PlaylistCarousel.displayName = 'PlaylistCarousel'

export default PlaylistCarousel
